/**
 * Header Component
 *
 * Bootstrap navbar with Trusscore branding.
 * Two icon buttons right-aligned: menu dropdown + user/login.
 * Mobile: menu items under hamburger, user icon stays visible.
 */

import { useTranslation } from 'react-i18next';
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  RefreshCcw,
  Upload,
  Download,
  ExternalLink,
  Info,
  Menu,
  Plus,
  Copy,
  User,
  UserPlus,
  UserPen,
  FilePen,
  LogIn,
  LogOut,
  Video,
  Shield,
  Lock,
  Terminal,
} from 'lucide-react';
import { useAppSettings } from '../../context/AppSettingsContext';
import { useAuth } from '../../context/AuthContext';
import { usePartnerBranding } from '../../context/PartnerBrandingContext';
import {
  saveEstimateData,
  loadEstimateData,
  performSaveWithData,
  buildEstimateDataFromWizardState,
} from '../../services/estimateDataManager';
import { useEstimator } from '../../context/EstimatorContext';
import { useSharedEstimateView } from '../../context/SharedEstimateViewContext';
import type { Currency, WizardState } from '../../types';
import { buildTemplateLoadPayload } from '../../utils/templatePayload';
import { markSplashSeen } from '../../utils/splashSeen';
import { setAdvancedDesignerFlag } from '../../utils/advancedDesignerFlag';
import { seedAdvancedState } from '../designer/advanced/advancedStorage';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { getRetailerAccountsEnabled } from '../../data/productTable';
import { LanguageSwitcher } from './LanguageSwitcher';
import { useSiteExperience } from '../../context/SiteExperienceContext';
import {
  BuiltinMenuKey,
  MenuRoute,
  SiteMenuEntry,
  getBuiltinMenuMeta,
  resolveSiteMenu,
} from '../../config/siteExperience';
import './Header.css';

export const Header: React.FC = () => {
  const { t } = useTranslation('common');
  const { state, reset, loadState } = useEstimator();
  const { view } = useSharedEstimateView();
  const { settings } = useAppSettings();
  const { isAuthenticated, user, login, signup, logout } = useAuth();
  const { isAdminAuthenticated, isSuperAdmin } = useAdminAuth();
  const { partner } = usePartnerBranding();
  const { config: siteExperience } = useSiteExperience();
  const location = useLocation();
  const navigate = useNavigate();

  // Whether to render any accounts UI at all. AuthContext already masks the
  // signed-IN surface, but these are the logged-OUT entry points — they render
  // precisely when the mask says "guest", so they need their own gate. A retailer
  // can suppress accounts on their own portal even with the global switch on.
  const accountsUiOn = settings.accountsEnabled && getRetailerAccountsEnabled();

  // Logo source: partner logo when this retailer is themed, otherwise the
  // default Trusscore wordmark. Alt text mirrors the same fallback.
  const logoSrc = partner?.logoUrl || '/images/trusscore-colour-light.svg';
  // partner.logoAlt / partner.name are database text and render as authored.
  const logoAlt = partner?.logoAlt || partner?.name || t('header.logoAlt');

  // Tutorial entry — only shown on the estimator nav (wizard + shared
  // estimate), never in admin, and only when an admin has configured a URL.
  const showTutorialEntry = !location.pathname.startsWith('/admin') && Boolean(settings.tutorialVideoUrl.trim());
  const openTutorial = () => window.dispatchEvent(new Event('show-tutorial-video'));

  // Route-aware menu rendering: the wizard lives at `/`, shared-estimate
  // views at `/estimate/:id` and `/mtl/:id`, admin at `/admin*`. Save/Load
  // Config + Reload only make sense on the wizard itself.
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isWizardRoute = location.pathname === '/';
  const isSharedEstimateRoute =
    location.pathname.startsWith('/estimate/') || location.pathname.startsWith('/mtl/');
  const hasSharedView = isSharedEstimateRoute && view !== null;
  const menuRoute: MenuRoute = isAdminRoute
    ? 'admin'
    : isWizardRoute
      ? 'wizard'
      : isSharedEstimateRoute
        ? 'shared'
        : location.pathname.startsWith('/account') ? 'account' : 'other';

  const handleReload = () => {
    // Only prompt if the user has actually started entering data — avoid
    // pestering on a fresh page.
    const hasProgress =
      state.completedSteps.length > 0 ||
      state.estimateResult !== null ||
      Boolean(state.projectInfo?.projectName);
    if (hasProgress && !window.confirm('Start over from step 1? Your current estimate progress will be lost.')) {
      return;
    }
    reset();
    // Suppress the one-shot splash for anyone landing on `/` from an
    // already-open tab — they've made a deliberate action to start fresh.
    markSplashSeen(null);
    // If we aren't on the wizard route, navigate there so the user
    // actually lands on step 1. Calling reset() alone on the shared-estimate
    // or admin routes just wipes context state nothing visible is reading.
    if (!isWizardRoute) {
      navigate('/');
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Logo click behaves like Reload / Start Over — intercept the anchor
  // navigation so middle-click / Ctrl-click still open a fresh tab at `/`,
  // but normal left-click runs the same confirm-and-reset flow as the menu.
  const handleLogoClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (e.defaultPrevented) return;
    // Let browsers open a new tab / context-menu actions through untouched.
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    e.preventDefault();
    handleReload();
  };

  const handleLoadConfig = () => {
    // When loading from a non-wizard route (SharedEstimate in particular),
    // dispatching LOAD_STATE updates context but the user stays on the
    // current page. Navigate to `/` so the wizard actually renders with
    // the loaded config. Splash is suppressed — deliberate action.
    loadEstimateData({
      afterLoad: () => {
        if (!isWizardRoute) {
          markSplashSeen(null);
          navigate('/');
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      },
    });
  };
  const handleSaveConfig = () => {
    // On the shared-estimate view, Save Current Config saves the VIEWED
    // estimate's config, not whatever wizard state is lingering in
    // EstimatorContext. Reuses the same SaveModal as the wizard by passing
    // options through — defaultName pre-fills with the stored estimate
    // name, onSave serializes the viewed payload instead of the singleton.
    if (hasSharedView && view) {
      const defaultName = view.estimateName || view.wizardState.projectInfo?.projectName || '';
      saveEstimateData({
        defaultName,
        onSave: (name: string) => {
          const data = buildEstimateDataFromWizardState(view.wizardState, name, true);
          performSaveWithData(data, name);
        },
      });
      return;
    }
    saveEstimateData();
  };

  /**
   * "New Estimate from this template" — hydrate the wizard from the viewed
   * estimate's config, strip out the original creator's personal info and
   * project name, mark all steps complete so the user can navigate freely,
   * and drop them on the last step before Calculate Estimate.
   */
  const handleNewFromTemplate = () => {
    if (!view) return;
    // An estimate made in the 3D designer is a template for the designer, not
    // the wizard: its room goes into Advanced mode as a fresh blueprint (the
    // visitor's own blueprints are replaced — a template is a new start), and
    // the Advanced route is opened for this browser if the admin keeps it off.
    if (view.designer) {
      seedAdvancedState(view.designer);
      setAdvancedDesignerFlag(true);
      markSplashSeen(null);
      navigate('/design');
      window.scrollTo({ top: 0 });
      return;
    }
    const wizardState = view.wizardState as WizardState & { currency?: Currency };
    loadState(buildTemplateLoadPayload(wizardState));
    // Suppress the splash — this is a deliberate, already-onboarded action.
    markSplashSeen(null);
    navigate('/');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Close the mobile hamburger menu after a selection. The menu is a Bootstrap
  // collapse opened via the data-api, so we trigger its own toggler to close it
  // — that keeps Bootstrap's state + slide animation in sync (no manual class
  // poking). No-ops when the menu is already collapsed (e.g. on desktop).
  const closeMobileMenu = () => {
    const menu = document.getElementById('navbarMobile');
    if (!menu || !menu.classList.contains('show')) return;
    const toggler = document.querySelector<HTMLElement>('.navbar-toggler[data-bs-target="#navbarMobile"]');
    toggler?.click();
  };

  const accountLabel = user?.name || user?.email || t('header.account');

  const runtimeBase = {
    route: menuRoute,
    accountsUiOn,
    isAuthenticated,
    isAdminAuthenticated,
    hasSharedView,
    tutorialAvailable: showTutorialEntry,
    splashAvailable: siteExperience.splash.enabled,
  };
  const mainEntries = resolveSiteMenu(siteExperience.menu, { ...runtimeBase, surface: 'main' });
  const accountEntries = resolveSiteMenu(siteExperience.menu, { ...runtimeBase, surface: 'account' });
  const mobileEntries = resolveSiteMenu(siteExperience.menu, { ...runtimeBase, surface: 'mobile' });

  const iconFor = (key: BuiltinMenuKey) => {
    const props = { size: 14 };
    switch (key) {
      case 'reload': return <RefreshCcw {...props} />;
      case 'create-estimate': return <Plus {...props} />;
      case 'new-from-template': return <Copy {...props} />;
      case 'load-config': return <Upload {...props} />;
      case 'save-config': return <Download {...props} />;
      case 'trusscore-link': return <ExternalLink {...props} />;
      case 'tutorial': return <Video {...props} />;
      case 'about': return <Info {...props} />;
      case 'sign-in': return <LogIn {...props} />;
      case 'sign-up': return <UserPlus {...props} />;
      case 'my-estimates': return <FilePen {...props} />;
      case 'my-information': return <UserPen {...props} />;
      case 'account-security': return <Lock {...props} />;
      case 'admin': return <Shield {...props} />;
      case 'sign-out': return <LogOut {...props} />;
    }
  };

  const runBuiltin = (key: BuiltinMenuKey) => {
    switch (key) {
      case 'reload': case 'create-estimate': handleReload(); break;
      case 'new-from-template': handleNewFromTemplate(); break;
      case 'load-config': handleLoadConfig(); break;
      case 'save-config': handleSaveConfig(); break;
      case 'tutorial': openTutorial(); break;
      case 'about': window.dispatchEvent(new Event('show-splash')); break;
      case 'sign-in': login(); break;
      case 'sign-up': signup(); break;
      case 'my-estimates': navigate('/account'); break;
      case 'my-information': navigate('/account/profile'); break;
      case 'account-security': navigate('/account/security'); break;
      case 'admin': navigate('/admin'); break;
      case 'sign-out': logout(); break;
      case 'trusscore-link': break;
    }
  };

  const renderMenuEntry = (entry: SiteMenuEntry, mobile: boolean) => {
    if (entry.type === 'divider') {
      return mobile
        ? <li key={entry.id}><hr style={{ borderColor: 'rgba(255,255,255,0.15)', margin: '0.25rem 0' }} /></li>
        : <li key={entry.id}><hr className="dropdown-divider" /></li>;
    }
    const cls = mobile ? 'nav-link btn btn-link text-start d-flex align-items-center gap-2' : 'dropdown-item d-flex align-items-center gap-2';
    if (entry.type === 'link') {
      return <li key={entry.id} className={mobile ? 'nav-item' : undefined}><a className={mobile ? 'nav-link d-flex align-items-center gap-2' : cls} href={entry.url} target={entry.target} rel={entry.target === '_blank' ? 'noopener noreferrer' : undefined}><ExternalLink size={14} /> {entry.label}</a></li>;
    }
    const meta = getBuiltinMenuMeta(entry.builtin);
    if (!meta) return null;
    // Precedence: the retailer's own menu_json label (database text, Phase 3)
    // → the admin's tutorial link text → our translated built-in default.
    const label = entry.label
      || (entry.builtin === 'tutorial' ? settings.tutorialMenuLinkText : '')
      || t(meta.labelKey);
    if (entry.builtin === 'trusscore-link') {
      return <li key={entry.id} className={mobile ? 'nav-item' : undefined}><a className={mobile ? 'nav-link d-flex align-items-center gap-2' : cls} href="https://trusscore.com" target="_blank" rel="noopener noreferrer">{iconFor(entry.builtin)} {label}</a></li>;
    }
    return <li key={entry.id} className={mobile ? 'nav-item' : undefined}><button className={cls} onClick={() => runBuiltin(entry.builtin)}>{iconFor(entry.builtin)} {label}</button></li>;
  };

  /**
   * Dev Console — super-admin only, and deliberately NOT part of the
   * configurable site menu. It is developer tooling, not retailer copy: a
   * saved menu_json must not be able to reorder it away, and a retailer that
   * saved their menu before this existed would otherwise never see it.
   *
   * It sits next to Admin in the account dropdown, which is where anyone looks
   * for it — but that dropdown only exists when the accounts system is on. In
   * password mode there is no person icon at all, so it falls back to the main
   * (☰) menu. One item, one home, reachable in both auth modes.
   */
  const devConsoleInAccountMenu = accountsUiOn && isAuthenticated;

  const renderDevConsoleEntry = (mobile: boolean) => {
    if (!isSuperAdmin) return null;
    const cls = mobile
      ? 'nav-link btn btn-link text-start d-flex align-items-center gap-2'
      : 'dropdown-item d-flex align-items-center gap-2';
    return (
      <>
        {mobile
          ? <li><hr style={{ borderColor: 'rgba(255,255,255,0.15)', margin: '0.25rem 0' }} /></li>
          : <li><hr className="dropdown-divider" /></li>}
        <li className={mobile ? 'nav-item' : undefined}>
          <button
            className={cls}
            onClick={() => window.dispatchEvent(new Event('toggle-dev-console'))}
          >
            <Terminal size={14} /> {t('menu.dev-console')}
          </button>
        </li>
      </>
    );
  };

  return (
    <>
      <header className="header sticky-top">
        <nav className="navbar navbar-dark">
          <div className="container">
            {/* Brand / Logo — left. Clicking it confirms + resets just like
                the Reload / Start Over menu item; Ctrl/⌘-click still opens a
                fresh tab at `/` so power-users aren't trapped. */}
            <a className="navbar-brand logo-link" href="/" onClick={handleLogoClick}>
              <img
                className="logo-img"
                src={logoSrc}
                alt={logoAlt}
              />
              <span className="logo-text">
                <span className="logo-trusscore">TRUSSCORE</span>
                <span className="logo-estimator">{t('header.estimator')}</span>
              </span>
            </a>

            {/* Right side — two icon buttons */}
            <div className="d-flex align-items-center gap-2">
              {/* Language — desktop only; self-hides when the portal offers
                  a single locale. */}
              <LanguageSwitcher variant="desktop" />

              {/* Menu dropdown — desktop: dropdown, mobile: hamburger menu */}
              <div className="dropdown d-none d-md-block">
                <button
                  type="button"
                  className="btn btn-outline-light btn-sm header-icon-btn"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                  title={t('header.menu')}
                >
                  <Menu size={18} />
                </button>
                <ul className="dropdown-menu dropdown-menu-end">
                  {mainEntries.map(entry => renderMenuEntry(entry, false))}
                  {!devConsoleInAccountMenu && renderDevConsoleEntry(false)}
                </ul>
              </div>

              {/* User / Login — desktop only (hidden when the accounts system is off) */}
              {accountsUiOn && accountEntries.length > 0 && (isAuthenticated ? (
                <div className="dropdown d-none d-md-block">
                  <button
                    type="button"
                    className="btn btn-outline-light btn-sm header-icon-btn d-flex"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                    title={accountLabel}
                  >
                    <User size={18} />
                  </button>
                  <ul className="dropdown-menu dropdown-menu-end">
                    <li><span className="dropdown-item-text small text-muted">{accountLabel}</span></li>
                    <li><hr className="dropdown-divider" /></li>
                    {accountEntries.map(entry => renderMenuEntry(entry, false))}
                    {devConsoleInAccountMenu && renderDevConsoleEntry(false)}
                  </ul>
                </div>
              ) : (
                <div className="dropdown d-none d-md-block">
                  <button
                    type="button"
                    className="btn btn-outline-light btn-sm header-icon-btn d-flex"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                    title={t('header.signInOrCreate')}
                  >
                    <User size={18} />
                  </button>
                  <ul className="dropdown-menu dropdown-menu-end">
                    {accountEntries.map(entry => renderMenuEntry(entry, false))}
                  </ul>
                </div>
              ))}

              {/* Hamburger — mobile only */}
              <button
                className="navbar-toggler d-md-none"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target="#navbarMobile"
                aria-controls="navbarMobile"
                aria-expanded="false"
                aria-label={t('header.toggleNav')}
              >
                <span className="navbar-toggler-icon"></span>
              </button>
            </div>

            {/* Mobile collapsed menu — mirrors the desktop dropdown, same
                route-aware branching. */}
            <div className="collapse navbar-collapse d-md-none" id="navbarMobile">
              {/* Any selection inside the menu collapses it (event delegation:
                  the item's own handler runs first, then this closes the menu). */}
              <ul className="navbar-nav" onClick={closeMobileMenu}>
                {mobileEntries.map(entry => renderMenuEntry(entry, true))}
                {renderDevConsoleEntry(true)}
                <LanguageSwitcher variant="mobile" />
              </ul>
            </div>
          </div>
        </nav>
      </header>
    </>
  );
};

export default Header;
